import { Input } from '../../primitives/input.js';
import { Checkbox } from '../../primitives/checkbox.js';
import { Label } from '../../primitives/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../primitives/select.js';
import { RelationPicker } from './relation-picker.js';
import { isMultiValue, isRangeValue } from '../list-state.js';
import type { FilterValue, ResourceFilter } from '../types.js';

export interface FilterControlProps {
  filter: ResourceFilter;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  /** Set by the filter bar so its `<Label>` names the control. */
  id?: string;
}

function asString(value: FilterValue): string {
  return typeof value === 'string' ? value : '';
}

function asStringArray(value: FilterValue): readonly string[] {
  return isMultiValue(value) ? value : [];
}

function asRange(value: FilterValue): { from?: string; to?: string } {
  return isRangeValue(value) ? value : {};
}

/**
 * One control per filter type. The type is what makes the filter bar uniform:
 * a screen declares "enum" and gets the same multi-select behaviour, keyboard
 * handling and empty semantics as every other enum filter in the product.
 */
export function FilterControl({ filter, value, onChange, id }: FilterControlProps) {
  if (filter.render) return <>{filter.render({ filter, value, onChange })}</>;

  switch (filter.type) {
    case 'text':
      return (
        <Input
          id={id}
          value={asString(value)}
          placeholder={filter.placeholder ?? filter.label}
          onChange={(event) => onChange(event.target.value || null)}
        />
      );

    case 'number':
      return (
        <Input
          id={id}
          type="number"
          min={filter.min}
          max={filter.max}
          step={filter.step}
          value={typeof value === 'number' ? value : ''}
          onChange={(event) =>
            onChange(event.target.value === '' ? null : Number(event.target.value))
          }
        />
      );

    case 'boolean':
      return (
        <Select
          value={typeof value === 'boolean' ? String(value) : ''}
          onValueChange={(next) => onChange(next === '' ? null : next === 'true')}
        >
          <SelectTrigger id={id} size="sm">
            <SelectValue placeholder="Qualsiasi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">{filter.trueLabel ?? 'Sì'}</SelectItem>
            <SelectItem value="false">{filter.falseLabel ?? 'No'}</SelectItem>
          </SelectContent>
        </Select>
      );

    case 'enum':
      if (filter.multiple) {
        const selected = asStringArray(value);
        return (
          <div
            id={id}
            role="group"
            aria-label={filter.label}
            className="grid max-h-64 gap-2 overflow-y-auto"
          >
            {filter.options.map((option) => {
              const checked = selected.includes(option.value);
              return (
                <Label key={option.value} className="font-normal text-ink-2">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => {
                      const next = checked
                        ? selected.filter((item) => item !== option.value)
                        : [...selected, option.value];
                      onChange(next.length > 0 ? next : null);
                    }}
                  />
                  {option.icon}
                  {option.label}
                </Label>
              );
            })}
          </div>
        );
      }
      return (
        <Select
          value={asString(value)}
          onValueChange={(next) => onChange(next === '' ? null : next)}
        >
          <SelectTrigger id={id} size="sm">
            <SelectValue placeholder="Qualsiasi" />
          </SelectTrigger>
          <SelectContent>
            {filter.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.icon}
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case 'dateRange': {
      const range = asRange(value);
      const emit = (next: { from?: string; to?: string }) =>
        onChange((next.from ?? next.to) ? next : null);
      return (
        <div id={id} role="group" aria-label={filter.label} className="grid grid-cols-2 gap-2">
          <div className="grid gap-1">
            <span className="text-label uppercase tracking-[0.08em] text-faint">Da</span>
            <Input
              type="date"
              value={range.from ?? ''}
              onChange={(event) => emit({ ...range, from: event.target.value || undefined })}
            />
          </div>
          <div className="grid gap-1">
            <span className="text-label uppercase tracking-[0.08em] text-faint">A</span>
            <Input
              type="date"
              value={range.to ?? ''}
              onChange={(event) => emit({ ...range, to: event.target.value || undefined })}
            />
          </div>
        </div>
      );
    }

    case 'relation':
      return (
        <RelationPicker
          id={id}
          value={asString(value) || null}
          search={filter.search}
          resolve={filter.resolve}
          placeholder={`Cerca ${filter.label.toLowerCase()}…`}
          onChange={(next) => onChange(next)}
        />
      );

    case 'custom':
      // Unreachable: `custom` requires `render`, which the guard above handles.
      return null;
  }
}
